import { useState, useEffect } from 'react';

/**
 * Sentinel returned when the blob rewrite fails so AdminMiniPlayer can
 * distinguish "still loading" (null) from "failed, use original URL" (BLOB_FAILED).
 */
export const BLOB_FAILED = '__BUNNY_BLOB_FAILED__';

/**
 * useBunnyHlsBlobUrl
 * ─────────────────────────────────────────────────────────────────────────────
 * Rewrites a BunnyCDN HLS master playlist (and every child manifest) into
 * blob: URLs where all child / segment paths are absolute and already carry
 * the same ?token= and ?expires= parameters.
 *
 * Why: HLS.js constructs child manifest / segment URLs as absolute paths from
 * relative entries, stripping query-string params in the process.  Any CDN
 * pull zone with token-auth enabled returns 403 on those unsigned child URLs.
 *
 * How: Three small HTTP fetches (master + each quality child manifest) happen
 * client-side before the player renders.  The player receives a blob: URL
 * whose manifest tree has all resource URLs pre-signed — no interceptors needed.
 *
 * Fallback strategy (NEW):
 *   If a child manifest fetch fails (CORS, 403, network), the signed child URL
 *   is embedded directly in the master blob instead of leaving an unresolvable
 *   placeholder string.  HLS.js can then fetch it with the token and quality
 *   levels are still detected from MANIFEST_PARSED.
 *
 * Return values:
 *   null          – rewrite still in progress
 *   BLOB_FAILED   – master manifest fetch failed (403 = bad token, CORS, etc.)
 *   string        – blob: URL ready for the player
 *
 * @param {string|null} signedUrl  Signed master playlist URL (must contain ?token=)
 * @returns {string|null}
 */
export function useBunnyHlsBlobUrl(signedUrl) {
    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        const needsRewrite =
            signedUrl &&
            signedUrl.toLowerCase().includes('.m3u8') &&
            signedUrl.includes('?token=');

        if (!needsRewrite) {
            setBlobUrl(null);
            return;
        }

        let cancelled = false;
        const createdUrls = [];

        async function buildManifestTree() {
            try {
                const parsedUrl  = new URL(signedUrl);
                const token      = parsedUrl.searchParams.get('token');
                const expires    = parsedUrl.searchParams.get('expires');
                const masterBase = signedUrl.split('?')[0].replace(/[^/]+$/, '');

                // 1. Fetch master playlist — explicit cors mode, credentials omitted
                const masterRes = await fetch(signedUrl, { mode: 'cors', credentials: 'omit' });
                if (cancelled) return;
                if (!masterRes.ok) {
                    const hint = masterRes.status === 403
                        ? 'Token may be invalid or the Bunny Zone Security Key is out of sync.'
                        : masterRes.status === 404
                            ? 'Video is still being processed by Bunny CDN. Try again in a moment.'
                            : `HTTP ${masterRes.status}`;
                    console.warn(`[useBunnyHlsBlobUrl] Master manifest fetch failed — ${hint}`, signedUrl);
                    setBlobUrl(BLOB_FAILED);
                    return;
                }

                const masterText = await masterRes.text();

                // 2. Collect child .m3u8 lines and rewrite them (placeholder first)
                const childEntries = [];
                const masterLines  = masterText.split('\n').map(line => {
                    const trimmed = line.trim();
                    if (!trimmed || trimmed.startsWith('#')) return line;
                    if (trimmed.toLowerCase().endsWith('.m3u8')) {
                        const absUrl      = trimmed.startsWith('http') ? trimmed.split('?')[0]
                            : new URL(trimmed, masterBase).href.split('?')[0];
                        const signedChild = `${absUrl}?token=${token}&expires=${expires}`;
                        const placeholder = `__NEXUS_CHILD_${childEntries.length}__`;
                        childEntries.push({ signedChildUrl: signedChild, placeholder });
                        return placeholder;
                    }
                    return line;
                });

                // 3. Fetch + rewrite all child manifests in parallel (explicit CORS mode)
                const childResults = await Promise.all(
                    childEntries.map(async ({ signedChildUrl, placeholder }) => {
                        try {
                            const childRes = await fetch(signedChildUrl, { mode: 'cors', credentials: 'omit' });
                            if (!childRes.ok) {
                                // Fetch failed — fall back to the signed URL so HLS.js
                                // can still detect quality levels from a valid URL.
                                console.warn(`[useBunnyHlsBlobUrl] Child manifest fetch failed (${childRes.status}), using signed URL fallback:`, signedChildUrl);
                                return { placeholder, childBlobUrl: null, fallbackUrl: signedChildUrl };
                            }
                            const childText = await childRes.text();
                            const childBase = signedChildUrl.split('?')[0].replace(/[^/]+$/, '');

                            const childLines = childText.split('\n').map(seg => {
                                const t = seg.trim();
                                if (!t) return seg;

                                // Rewrite #EXT-X-MAP:URI (fMP4 / CMAF init segment).
                                // Without this, hls.js resolves the relative URI against the blob: base URL
                                // → wrong host → init segment 404 → quality level fails (breaks 4K/CMAF).
                                if (t.startsWith('#EXT-X-MAP:')) {
                                    return seg.replace(/URI="([^"]+)"/, (_match, uri) => {
                                        if (uri.startsWith('http')) {
                                            return `URI="${uri.split('?')[0]}?token=${token}&expires=${expires}"`;
                                        }
                                        const absUri = new URL(uri, childBase).href.split('?')[0];
                                        return `URI="${absUri}?token=${token}&expires=${expires}"`;
                                    });
                                }

                                if (t.startsWith('#')) return seg;
                                const lower = t.toLowerCase();
                                if (lower.endsWith('.ts') || lower.endsWith('.m4s') || lower.endsWith('.mp4') || lower.endsWith('.aac')) {
                                    const absUrl = t.startsWith('http') ? t.split('?')[0]
                                        : new URL(t, childBase).href.split('?')[0];
                                    return `${absUrl}?token=${token}&expires=${expires}`;
                                }
                                return seg;
                            });

                            const childBlob    = new Blob([childLines.join('\n')], { type: 'application/vnd.apple.mpegurl' });
                            // Append #playlist.m3u8 fragment so detectSourceType() sees '.m3u8' → HLS mode + quality selector.
                            // Browsers strip the fragment before fetching the blob, so manifest content is unaffected.
                            const childBlobUrl = URL.createObjectURL(childBlob) + '#playlist.m3u8';
                            return { placeholder, childBlobUrl, fallbackUrl: null };
                        } catch (fetchErr) {
                            // Network / CORS error — use signed URL as fallback
                            console.warn('[useBunnyHlsBlobUrl] Child manifest fetch threw, using signed URL fallback:', fetchErr.message, signedChildUrl);
                            return { placeholder, childBlobUrl: null, fallbackUrl: signedChildUrl };
                        }
                    })
                );

                if (cancelled) return;

                // 4. Substitute child blob: URLs (or signed URL fallback) into master.
                //    NEVER leave a placeholder — invalid placeholder strings cause HLS.js
                //    to fail manifest parsing and quality levels disappear from the player.
                let finalMaster = masterLines.join('\n');
                for (const result of childResults) {
                    if (!result) continue;
                    const replacement = result.childBlobUrl || result.fallbackUrl;
                    if (replacement) {
                        if (result.childBlobUrl) {
                            createdUrls.push(result.childBlobUrl.split('#')[0]); // raw URL (no fragment) for revokeObjectURL
                        }
                        finalMaster = finalMaster.replace(result.placeholder, replacement);
                    }
                }

                // 5. Create master blob: URL
                const masterBlob       = new Blob([finalMaster], { type: 'application/vnd.apple.mpegurl' });
                const rawMasterBlobUrl = URL.createObjectURL(masterBlob);
                // #playlist.m3u8 fragment: player sees '.m3u8' → HLS mode + quality selector
                const masterBlobUrl    = rawMasterBlobUrl + '#playlist.m3u8';
                createdUrls.push(rawMasterBlobUrl); // track raw URL for cleanup

                if (!cancelled) setBlobUrl(masterBlobUrl);

            } catch (err) {
                if (!cancelled) {
                    // CORS error, network failure, or malformed URL
                    console.warn('[useBunnyHlsBlobUrl] manifest rewrite failed:', err.message,
                        '\nHint: If this is a CORS error, check that the Bunny CDN pull zone',
                        'has "Add CORS Headers" enabled in the pull zone settings.');
                    setBlobUrl(BLOB_FAILED);
                }
            }
        }

        buildManifestTree();

        return () => {
            cancelled = true;
            createdUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch { /* ignore */ } });
            setBlobUrl(null);
        };
    }, [signedUrl]);

    return blobUrl;
}
